import React, { type ComponentProps, type ElementType } from 'react';

import { Link } from '@/shared/ui/primitives/Link';

import { Z_INDEX } from '@/config/z-index';
import Markdown from '@/features/_shared/Markdown';
import WidgetMapper from '@/features/ai-chat/components/WidgetMapper/WidgetMapper';
import { GX_REGEX } from '@/features/ai-chat/lib/gxCodes';
import type { AIMessage, ToolResultContentPart } from '@/features/ai-chat/types/chat.types';
import Tooltip from '@/shared/ui/primitives/Tooltip';

import type { ExtraProps } from 'react-markdown';

const getPartKey = (part: AIMessage['content'][number], index: number): string => {
  if (part.kind === 'text') return `text-${index}`;
  if (part.kind === 'tool_call') return `tool-call-${part.id ?? part.name}-${index}`;
  if (part.kind === 'tool_result') return `tool-result-${part.tool_call_id}-${index}`;
  if (part.kind === 'notice') return `notice-${index}`;
  return `content-part-${index}`;
};

const normalizeWidgetIdPart = (value: string): string => value.replace(/[^a-zA-Z0-9_-]/g, '-');

const withStaticChartWidgetId = (
  part: ToolResultContentPart,
  index: number,
  msg: AIMessage
): ToolResultContentPart => {
  if (part.display !== 'static_chart' || part.widgetId != null) {
    return part;
  }

  return {
    ...part,
    widgetId: normalizeWidgetIdPart(`${msg.id}-${msg.timestamp}-${part.tool_call_id}-${index}`)
  };
};

const AssistantLink: ElementType<ComponentProps<'a'> & ExtraProps> = ({
  node,
  children,
  ...props
}) => (
  <Tooltip
    sx={{
      zIndex: Z_INDEX.panel
    }}
    title='Click to see index information'
    variant='secondary'
  >
    <Link
      {...props}
      color='textPrimary'
      rel='noopener noreferrer'
      sx={{ textDecorationColor: 'inherit' }}
      target='_blank'
    >
      {children}
    </Link>
  </Tooltip>
);

const AIContent = ({ msg }: { msg: AIMessage }) => {
  let staticChartOrdinal = 0;
  return (
    <>
      {msg.content.map((part, index) => {
        switch (part.kind) {
          case 'text':
            return (
              <Markdown
                key={getPartKey(part, index)}
                customMarkdown={{
                  a: AssistantLink
                }}
              >
                {part.text.replace(GX_REGEX, match => `[${match}](/${match})`)}
              </Markdown>
            );
          case 'tool_result':
            if (part.display === 'default') return null;
            return (
              <WidgetMapper
                key={getPartKey(part, index)}
                widgetData={withStaticChartWidgetId(
                  part,
                  part.display === 'static_chart' ? staticChartOrdinal++ : 0,
                  msg
                )}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
};

export default AIContent;
